import { type PlaceSiteTypeId, placeSiteTypeId } from '../constants/placeSiteTypes';
import type { PointOfInterestPlace } from './pointOfInterest';

// Mixpanel event names for basemap places. Centralised here so every emit
// site references the same string constant.

export const POI_MIXPANEL_EVENTS = {
  /** A place card opened from a basemap click. */
  INSPECTED: 'poi_inspected',
  /** The card's Google Maps link was followed. */
  GOOGLE_MAPS_OPENED: 'poi_google_maps_opened',
  /** The card's Create location button was pressed. Fires whether or not a location follows. */
  CREATE_LOCATION_CLICKED: 'poi_create_location_clicked',
  /** The place-chip selection settled (debounced). */
  FILTERS_APPLIED: 'poi_filters_applied',
} as const;

export type PoiMixpanelEvent = (typeof POI_MIXPANEL_EVENTS)[keyof typeof POI_MIXPANEL_EVENTS];

/** Shared place dimensions. The name and the point stay off the event. */
export interface PoiPlacePayload {
  maki: string;
  /** The filter chip this icon belongs to. */
  site_type: PlaceSiteTypeId;
  /** Basemap category. Absent when the place has none. */
  place_class?: string;
}

export interface PoiInspectedPayload extends PoiPlacePayload {
  /** Opening time in Mixpanel's epoch seconds, before the asynchronous directory check. */
  time: number;
  /** The directory verdict for this opening. Absent when verification fails. */
  is_verified?: boolean;
}

export type PoiCreateLocationClickedPayload = PoiPlacePayload;

export interface PoiGoogleMapsOpenedPayload extends PoiPlacePayload {
  /**
   * Whether the place directory confirmed the place. Absent while that check
   * is in flight, including a refresh of a cached answer.
   */
  is_verified?: boolean;
}

export interface PoiFiltersAppliedPayload {
  /** The chips left visible, in chip order. */
  visible_types: PlaceSiteTypeId[];
  visible_count: number;
  hidden_count: number;
  /** One chip, or the section eye. `group` matches `map_layer_toggled` for that control. */
  scope: 'single' | 'group';
}

/** Ticking several chips is several calls but one intent, so the emit carries the settled selection. */
export const POI_FILTER_TRACKING_DEBOUNCE_MS = 500;

export function poiPlacePayload(place: PointOfInterestPlace): PoiPlacePayload {
  const payload: PoiPlacePayload = {
    maki: place.maki,
    site_type: placeSiteTypeId(place.maki),
  };
  if (place.placeClass !== '') payload.place_class = place.placeClass;
  return payload;
}
